import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

// SomaGuard: variant responses, keyed by request URL. Hovering (or pressing) an option starts its
// request early, so most clicks find the response already here; a repeat pick is instant.
/** @type {Map<string, Promise<string>>} */
const sgResponses = new Map();

/** @type {Set<string>} */
const sgWarmedImages = new Set();

/**
 * SomaGuard: downloads images into the browser cache, three at a time, with the same srcset/sizes
 * the gallery uses so the browser picks the same file it will show.
 * @param {Element[]} sources
 */
async function sgPreloadImages(sources) {
  const queue = sources.filter((el) => {
    const key = el.getAttribute('srcset') || el.getAttribute('src');
    if (!key || sgWarmedImages.has(key)) return false;
    sgWarmedImages.add(key);
    return true;
  });
  const worker = async () => {
    while (queue.length) {
      const el = /** @type {Element} */ (queue.shift());
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = img.onerror = resolve;
        const sizes = el.getAttribute('sizes');
        if (sizes) img.sizes = sizes;
        const srcset = el.getAttribute('srcset');
        if (srcset) img.srcset = srcset;
        img.src = el.getAttribute('src') || '';
      });
    }
  };
  await Promise.all([worker(), worker(), worker()]);
}

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function sgFetchText(url) {
  let pending = sgResponses.get(url);
  if (!pending) {
    pending = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      return response.text();
    });
    pending.catch(() => sgResponses.delete(url));
    sgResponses.set(url, pending);
    if (sgResponses.size > 40) sgResponses.delete(sgResponses.keys().next().value);
  }
  return pending;
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #latestRequestUrl;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('pointerover', this.#prefetchOption);
    this.addEventListener('pointerdown', this.#prefetchOption);
    this.addEventListener('focusin', this.#prefetchOption);
    this.#scheduleWarm();
  }

  #warmScheduled = false;

  /** SomaGuard: once the page (and its own photos) has loaded, warm the options one click away. */
  #scheduleWarm() {
    if (this.#warmScheduled || !this.#mainSectionId()) return;
    this.#warmScheduled = true;
    const run = () => {
      this.#warmScheduled = false;
      this.#warmNeighbors();
    };
    const idle = () =>
      'requestIdleCallback' in window ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 500);
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  /**
   * SomaGuard: fetches each one-click-away option's section, then downloads every option's main
   * photo before any of their other photos, so a first pick shows its photo at once.
   */
  async #warmNeighbors() {
    // @ts-ignore - navigator.connection is not in every lib.dom
    if (navigator.connection?.saveData) return;
    const inputs = Array.from(this.querySelectorAll('fieldset input[data-option-value-id]'));
    const urls = [...new Set(inputs.map((input) => this.#requestUrlFor(input)).filter(Boolean))];
    if (!urls.length) return;
    const docs = await Promise.all(
      urls.map((url) =>
        sgFetchText(url)
          .then((text) => new DOMParser().parseFromString(text, 'text/html'))
          .catch(() => null)
      )
    );
    const thumbsShown = Array.from(document.querySelectorAll('media-gallery .slideshow-controls__thumbnails img')).some(
      (img) => img instanceof HTMLElement && img.offsetParent !== null
    );
    const heroes = [];
    const rest = [];
    for (const doc of docs) {
      if (!doc) continue;
      const slides = Array.from(doc.querySelectorAll('media-gallery slideshow-slide img'));
      if (slides[0]) heroes.push(slides[0]);
      rest.push(...slides.slice(1));
      if (thumbsShown) rest.push(...doc.querySelectorAll('media-gallery .slideshow-controls__thumbnails img'));
    }
    await sgPreloadImages(heroes);
    await sgPreloadImages(rest);
  }

  /**
   * SomaGuard: the request URL a pick of this option would make, or '' when it is not a
   * same-product option of the PDP's main picker.
   * @param {Element} input
   * @returns {string}
   */
  #requestUrlFor(input) {
    if (!(input instanceof HTMLInputElement) || input.checked || input.disabled) return '';
    if (input.dataset.connectedProductUrl) return '';
    const sectionId = this.#mainSectionId();
    if (!sectionId || !this.dataset.productUrl) return '';

    const fieldset = input.closest('fieldset');
    const checked = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    if (!checked.some((option) => option.closest('fieldset') === fieldset)) return '';
    const values = checked.map((option) =>
      option.closest('fieldset') === fieldset ? input.dataset.optionValueId : option.dataset.optionValueId
    );
    if (values.some((value) => !value)) return '';

    let url = `${this.dataset.productUrl}?section_id=${sectionId}&option_values=${values.join(',')}`;
    const sgFallbackId = input.dataset.sgFallbackVariantId;
    if (sgFallbackId && !input.dataset.variantId) {
      const fallback = new URL(url, window.location.origin);
      fallback.searchParams.delete('option_values');
      fallback.searchParams.set('variant', sgFallbackId);
      url = `${fallback.pathname}${fallback.search}`;
    }
    return url;
  }

  /**
   * SomaGuard: the product section's id when this picker is the PDP's main picker, else ''.
   * Asking for just this section (Section Rendering API) returns about a fifth of the page.
   * @returns {string}
   */
  #mainSectionId() {
    if (this.dataset.templateProductMatch !== 'true') return '';
    if (this.closest('product-card, quick-add-dialog, quick-add-component, swatches-variant-picker-component')) return '';
    const id = this.closest('.shopify-section')?.id || '';
    return id.startsWith('shopify-section-') ? id.slice('shopify-section-'.length) : '';
  }

  /**
   * SomaGuard: starts the request a pick of the hovered/pressed option would make.
   * @param {Event} event
   */
  #prefetchOption = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const input = target?.closest('label')?.querySelector('input[data-option-value-id]');
    const url = input ? this.#requestUrlFor(input) : '';
    if (url) sgFetchText(url).catch(() => {});
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    let requestUrl = this.buildRequestUrl(selectedOption);
    let variantId = selectedOption.dataset.variantId || null;

    // SomaGuard: a first-option value whose combination with the other selected options does not
    // exist carries data-sg-fallback-variant-id (server-side, variant-main-picker.liquid). Request
    // that variant instead of the dead option_values combo so the shopper never lands on
    // "Unavailable" by changing Size; the server re-render resets the dependent option (Pack).
    const sgFallbackId = selectedOption.dataset.sgFallbackVariantId;
    if (sgFallbackId && !variantId) {
      const fallback = new URL(requestUrl, window.location.origin);
      fallback.searchParams.delete('option_values');
      fallback.searchParams.set('variant', sgFallbackId);
      requestUrl = `${fallback.pathname}${fallback.search}`;
      variantId = sgFallbackId;
    }

    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    const sectionId = selectedOption.dataset.connectedProductUrl ? '' : this.#mainSectionId();
    if (sectionId) {
      return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#latestRequestUrl = requestUrl;

    const responseText = requestUrl.includes('section_id=') && !requestUrl.includes('section-rendering-product-card')
      ? sgFetchText(requestUrl)
      : fetch(requestUrl, { signal: this.#abortController.signal }).then((response) => response.text());

    responseText
      .then((responseText) => {
        if (requestUrl !== this.#latestRequestUrl) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#scheduleWarm();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
